// components/portal/folderBrowserUI.tsx
// Shared building blocks for the "Database" folder browsers (Assets / Tenants / Contacts):
// a breadcrumb, a responsive folder grid, and a folder card. Kept visually identical to the
// original tenants-by-region browser.
'use client';

import React from 'react';
import { ChevronRight, Search } from '@/components/ui/lucide-memo';

/**
 * Jump straight to a record instead of drilling the folders.
 *
 * The folder tree is the right default for browsing, but it is the wrong tool when you already
 * know the name you want: three clicks to reach a record you could have typed. A non-empty query
 * therefore REPLACES the folders with the flat table, filtered server-side, and clearing it puts
 * the folders back. Same control, same place, in every browser of the Database section.
 */
export const FolderSearch = ({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) => (
  // ChatGPT's round outlined search field, the page headers' own.
  <div className="relative px-6 py-3">
    <Search size={16} className="pointer-events-none absolute left-[38px] top-1/2 -translate-y-1/2 text-[#AFAFAF]" />
    <input
      type="text"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      aria-label={placeholder}
      className="h-9 w-full max-w-[420px] rounded-full border border-[#2A2A2A] bg-transparent pl-10 pr-3 text-[#E5E5E5] outline-none transition-colors placeholder:text-[#AFAFAF] focus:border-[#444444]"
      style={{ fontSize: 14 }}
    />
  </div>
);

/**
 * The page's name as ChatGPT sets it (owner, 2026-09-28): a 26px heading with no band under it. A
 * nested folder keeps its trail, the parents quiet and clickable, the current one the heading.
 */
export const Breadcrumb = ({ items }: { items: Array<{ label: string; onClick?: () => void }> }) => (
  <div className="flex min-w-0 flex-wrap items-center gap-2 px-6 pb-2 pt-6">
    {items.map((it, i) => (
      <React.Fragment key={i}>
        {i > 0 && <ChevronRight size={20} className="shrink-0 text-[#5D5D5D]" />}
        {it.onClick ? (
          <button onClick={it.onClick} className="font-medium text-[#8E8E8E] transition-colors hover:text-white" style={{ fontSize: 26, lineHeight: '32px' }}>{it.label}</button>
        ) : (
          <h1 className="min-w-0 truncate font-medium text-white" style={{ fontSize: 26, lineHeight: '32px' }}>{it.label}</h1>
        )}
      </React.Fragment>
    ))}
  </div>
);

export const FolderGrid = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-0 flex-1 overflow-y-auto custom-scrollbar px-6 py-3">
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">{children}</div>
  </div>
);

export const FolderCard = ({ icon, emoji, title, subtitle, onClick }: {
  icon?: React.ReactNode; emoji?: string; title: string; subtitle: string; onClick: () => void;
}) => (
  <button
    onClick={onClick}
    // ChatGPT's project row: a #1B1B1B card with a #262626 rim, a round glyph, the name and one grey line.
    className="group flex items-center gap-3 rounded-[16px] border border-[#262626] bg-[#1B1B1B] px-3.5 py-3 text-left transition-colors hover:bg-[#212121]"
  >
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#262626] text-[18px] leading-none">
      {emoji ?? icon}
    </span>
    <span className="min-w-0">
      <span className="block truncate font-medium text-[#ECECEC]" style={{ fontSize: 14, lineHeight: '20px' }}>{title}</span>
      <span className="block truncate text-[#8E8E8E]" style={{ fontSize: 13, lineHeight: '18px' }}>{subtitle}</span>
    </span>
  </button>
);
